import { monsterPhotos, monsterSheets, MONSTER_SHEET_SIZE, photoFocus, photosNeedingReplacement, rectInBounds } from '../data/guide/monsterPhotoSheets'
import { extraMonsterPhotos, extraMonsterSheets, EXTRA_SHEET_SIZE, extraPhotoCount, extraRectInBounds } from '../data/guide/monsterExtraPhotoSheets'
import { resolveImageEntries } from './entityImages'
import { monsterKind } from './monsterKind'
import type { WorldData } from '../types/world'

/*
 * Diagnóstico ÚNICO de las fotos de monstruos de los dos paquetes:
 * - VANGUARD_MOBS_LIMPIOS (10 láminas, 154 IDs de referencia)
 * - VANGUARD_MONSTRUOS_EXTRA (3 láminas, 131 IDs de referencia, 39 fotos)
 * Cuatro etapas separadas: recibido → cargado → asignado → visible.
 * Tres controles independientes: carga de la lámina, correspondencia de identidad y encuadre.
 * Clasificación NPC / monstruo / objeto por clasificación documentada; nunca por ID alto, nivel ni falta de foto.
 */

export type SheetLoad = { status: 'cargando' | 'ok' | 'error'; w?: number; h?: number }
export type PackKey = 'inicial' | 'extra'
export type EntityClass = 'npc' | 'monstruo' | 'objeto'

/** Objetos con clasificación documentada (staff o exportación). La exportación del 08/10/2026 no trae ese campo. */
export const documentedObjectIds = new Set<number>()

export const PACKS: { key: PackKey; name: string; sheets: Record<number, string>; size: number; reference: { ids: number; photos?: number; sheets: number } }[] = [
  { key: 'inicial', name: 'VANGUARD_MOBS_LIMPIOS', sheets: monsterSheets, size: MONSTER_SHEET_SIZE, reference: { ids: 154, sheets: 10 } },
  { key: 'extra', name: 'VANGUARD_MONSTRUOS_EXTRA', sheets: extraMonsterSheets, size: EXTRA_SHEET_SIZE, reference: { ids: 131, photos: 39, sheets: 3 } },
]

/** IDs que el staff pidió revisar expresamente. */
export const AUDIT_CHECK_IDS = [313, 420, 315, 460, 461, 462, 407]

export const sheetKey = (pack: PackKey, sheet: number) => `${pack}:${sheet}`

type PackEntry = { pack: PackKey; id: number; sheet: number; sourceName: string; inBounds: boolean; manualFocus: boolean; sharedWith: number[]; replace?: string }

const replaceReason = new Map(photosNeedingReplacement.map((p) => [p.id, p.reason]))

const entries: PackEntry[] = [
  ...monsterPhotos.map((p) => ({ pack: 'inicial' as const, id: p.id, sheet: p.sheet, sourceName: p.sourceName, inBounds: rectInBounds(p), manualFocus: !!photoFocus[p.id], sharedWith: [], replace: replaceReason.get(p.id) })),
  ...extraMonsterPhotos.map((p) => ({ pack: 'extra' as const, id: p.id, sheet: p.sheet, sourceName: p.sourceName, inBounds: extraRectInBounds(p), manualFocus: p.focus.zoom !== 1 || p.focus.fx !== 0.5 || p.focus.fy !== 0.5, sharedWith: p.sharedWith })),
]

/** De qué registro sale la foto que usa la guía (misma resolución que las fichas: utils/entityImages). */
function imageOrigin(key: string | undefined, origin: string | undefined): 'staff' | PackKey | 'npc' | undefined {
  if (!key) return undefined
  if (key.startsWith('paquete-extra:')) return 'extra'
  if (key.startsWith('paquete-npc:')) return 'npc'
  if (key.startsWith('paquete:')) return 'inicial'
  return origin === 'staff' || origin ? 'staff' : undefined
}

/** Normaliza para comparar nombres: sin paréntesis (nivel), signos ni espacios. */
const norm = (s: string) => s.toLowerCase().replace(/\(.*?\)/g, '').replace(/[^a-z0-9]/g, '')

export function entityClass(w: WorldData, id: number): EntityClass {
  const m = w.monsters.get(id)
  if (m && monsterKind(w, m) === 'npc') return 'npc'
  if (documentedObjectIds.has(id)) return 'objeto'
  return 'monstruo'
}

export type AuditRow = {
  pack: PackKey
  id: number
  sourceName: string
  sheet: number
  catalogName?: string
  cls?: EntityClass
  /** Identidad: el ID existe y el nombre de la lámina coincide con 06_monsters. */
  identity: 'coincide' | 'nombre distinto' | 'no está en 06_monsters'
  /** Carga de su lámina. */
  load: SheetLoad['status'] | 'sin comprobar'
  /** Encuadre: recorte dentro de su celda, foco manual y reemplazo recomendado. */
  framing: 'correcto' | 'fuera de su celda' | 'reemplazo recomendado'
  framingNote?: string
  /** Qué foto usa la guía para este ID. */
  assigned: 'este paquete' | 'foto del staff (conservada)' | 'paquete inicial (conservada)' | 'láminas NPC' | 'sin asignar'
  visible: 'sí' | 'no' | 'sin comprobar'
  sharedWith: number[]
}

export function buildMonsterPhotoAudit(w: WorldData, sheets: Record<string, SheetLoad>, run: boolean) {
  const { byMonster } = resolveImageEntries(w)
  const rows: AuditRow[] = entries.map((e) => {
    const m = w.monsters.get(e.id)
    const entry = byMonster.get(e.id)
    const img = entry?.image
    const identity: AuditRow['identity'] = !m ? 'no está en 06_monsters' : norm(m.name) === norm(e.sourceName) || norm(m.name).startsWith(norm(e.sourceName)) || norm(e.sourceName).startsWith(norm(m.name)) ? 'coincide' : 'nombre distinto'
    const s = sheets[sheetKey(e.pack, e.sheet)]
    const load: AuditRow['load'] = run ? s?.status ?? 'cargando' : 'sin comprobar'
    const origin = imageOrigin(entry?.key, img?.origin)
    const assigned: AuditRow['assigned'] = !m || !img
      ? 'sin asignar'
      : origin === 'staff'
        ? 'foto del staff (conservada)'
        : origin === e.pack
          ? 'este paquete'
          : origin === 'inicial'
            ? 'paquete inicial (conservada)'
            : 'láminas NPC'
    const framing: AuditRow['framing'] = !e.inBounds ? 'fuera de su celda' : e.replace ? 'reemplazo recomendado' : 'correcto'
    const visible: AuditRow['visible'] = assigned !== 'este paquete' ? 'no' : !run ? 'sin comprobar' : load === 'ok' && e.inBounds ? 'sí' : 'no'
    return {
      pack: e.pack,
      id: e.id,
      sourceName: e.sourceName,
      sheet: e.sheet,
      catalogName: m?.name,
      cls: m ? entityClass(w, e.id) : undefined,
      identity,
      load,
      framing,
      framingNote: [e.replace, e.manualFocus ? 'encuadre manual por ID' : '', e.sharedWith.length ? `foto compartida con ${e.sharedWith.join(', ')} (referencia de familia)` : ''].filter(Boolean).join(' · ') || undefined,
      assigned,
      visible,
      sharedWith: e.sharedWith,
    }
  })

  const packs = PACKS.map((p) => {
    const pr = rows.filter((r) => r.pack === p.key)
    const ids = new Set(pr.map((r) => r.id))
    const photos = p.key === 'extra' ? extraPhotoCount : ids.size
    const sheetIds = Object.keys(p.sheets).map(Number)
    const loads = sheetIds.map((n) => ({ n, s: sheets[sheetKey(p.key, n)] }))
    return {
      ...p,
      rows: pr,
      received: { sheets: sheetIds.length, ids: ids.size, photos },
      loaded: {
        ok: loads.filter((l) => l.s?.status === 'ok').length,
        failed: loads.filter((l) => l.s?.status === 'error').map((l) => l.n),
        resized: loads.filter((l) => l.s?.status === 'ok' && (l.s.w !== p.size || l.s.h !== p.size)).map((l) => `${l.n}: ${l.s!.w}×${l.s!.h}`),
      },
      assigned: {
        thisPack: pr.filter((r) => r.assigned === 'este paquete').length,
        keptStaff: pr.filter((r) => r.assigned === 'foto del staff (conservada)').length,
        keptOther: pr.filter((r) => r.assigned === 'paquete inicial (conservada)' || r.assigned === 'láminas NPC').length,
        notInCatalog: pr.filter((r) => r.identity === 'no está en 06_monsters').length,
      },
      visible: run ? pr.filter((r) => r.visible === 'sí').length : undefined,
      identityIssues: pr.filter((r) => r.identity !== 'coincide'),
      framingIssues: pr.filter((r) => r.framing !== 'correcto'),
      loadIssues: pr.filter((r) => r.load === 'error'),
    }
  })

  const unionIds = new Set(rows.map((r) => r.id))
  const overlap = Array.from(new Set(rows.filter((r) => r.pack === 'extra').map((r) => r.id))).filter((id) => rows.some((r) => r.pack === 'inicial' && r.id === id))

  // Pendientes recalculados sobre TODO el catálogo publicado (06_monsters completo; sin excluir por ID, nivel ni foto).
  const all = Array.from(w.monsters.values()).sort((a, b) => a.id - b.id)
  const withImage = all.filter((m) => byMonster.get(m.id)?.image)
  const pending = all.filter((m) => !byMonster.get(m.id)?.image).map((m) => ({ id: m.id, name: m.name, cls: entityClass(w, m.id) }))
  const byClass = (c: EntityClass) => ({ total: all.filter((m) => entityClass(w, m.id) === c).length, withImage: withImage.filter((m) => entityClass(w, m.id) === c).length, pending: pending.filter((p) => p.cls === c) })

  const checks = AUDIT_CHECK_IDS.map((id) => {
    const m = w.monsters.get(id)
    const inPacks = rows.filter((r) => r.id === id)
    const entry = byMonster.get(id)
    const img = entry?.image
    const origin = imageOrigin(entry?.key, img?.origin)
    return {
      id,
      name: m?.name,
      cls: m ? entityClass(w, id) : undefined,
      received: inPacks.length ? inPacks.map((r) => `${r.pack === 'inicial' ? 'LIMPIOS' : 'EXTRA'} lámina ${r.sheet} («${r.sourceName}»)`).join(' + ') : 'no está en ningún paquete',
      load: inPacks.map((r) => r.load).join(', ') || '—',
      identity: inPacks.map((r) => r.identity).join(', ') || (m ? '—' : 'no está en 06_monsters'),
      framing: inPacks.map((r) => `${r.framing}${r.framingNote ? ` (${r.framingNote})` : ''}`).join(', ') || '—',
      assigned: !img ? 'sin foto (marcador neutro)' : origin === 'staff' ? 'foto del staff' : origin === 'inicial' ? 'paquete LIMPIOS' : origin === 'extra' ? 'paquete EXTRA' : 'láminas NPC',
      visible: inPacks.find((r) => r.assigned === 'este paquete')?.visible ?? (origin === 'staff' ? 'foto del staff (carga no medida aquí)' : origin === 'npc' ? 'láminas NPC (ver su panel)' : 'no'),
    }
  })

  return { rows, packs, unionIds: unionIds.size, overlap, catalogTotal: all.length, withImage: withImage.length, pending, byClass, checks }
}

export type MonsterPhotoAudit = ReturnType<typeof buildMonsterPhotoAudit>

const clsLabel: Record<EntityClass, string> = { npc: 'NPC', monstruo: 'Monstruo', objeto: 'Objeto' }

export function formatMonsterPhotoAudit(a: MonsterPhotoAudit, run: boolean): string {
  const L: string[] = [
    'DIAGNÓSTICO DE FOTOS DE MONSTRUOS — paquetes LIMPIOS + EXTRA',
    `Calculado: ${new Date().toLocaleString('es-ES')} en este navegador. ${run ? '' : 'Láminas SIN comprobar: pulsa «Comprobar las 13 láminas».'}`,
    'Índices: el JSON compacto (VANGUARD_MONSTRUOS_INDICE_COMPACTO.json) no llegó al proyecto; los IDs se leyeron de los rótulos impresos en las láminas.',
    '',
  ]
  a.packs.forEach((p) => {
    L.push(
      `## ${p.name}`,
      `1. Recibido: ${p.received.sheets} láminas · ${p.received.ids} IDs · ${p.received.photos} fotos distintas (referencia del índice: ${p.reference.sheets} láminas · ${p.reference.ids} IDs${p.reference.photos ? ` · ${p.reference.photos} fotos` : ''})${p.received.ids !== p.reference.ids ? ` — DIFERENCIA de ${p.reference.ids - p.received.ids} IDs` : ''}`,
      `2. Cargado: ${run ? `${p.loaded.ok}/${p.received.sheets} láminas${p.loaded.failed.length ? ` · no cargan: ${p.loaded.failed.join(', ')}` : ''}${p.loaded.resized.length ? ` · tamaño distinto de ${p.size}: ${p.loaded.resized.join('; ')} (recorte escalado)` : ''}` : 'sin comprobar'}`,
      `3. Asignado: ${p.assigned.thisPack} IDs usan esta foto · ${p.assigned.keptStaff} conservan la foto del staff · ${p.assigned.keptOther} conservan otra lámina · ${p.assigned.notInCatalog} no están en 06_monsters`,
      `4. Visible: ${p.visible ?? 'sin comprobar'}${run ? ` de ${p.assigned.thisPack}` : ''}`,
      `Control de carga: ${run ? (p.loadIssues.length ? `${p.loadIssues.length} IDs afectados` : 'sin errores') : 'sin comprobar'}`,
      `Control de identidad: ${p.identityIssues.length ? `${p.identityIssues.length} a revisar` : 'todos coinciden'}`,
      ...p.identityIssues.map((r) => `  - ${r.id}\tlámina «${r.sourceName}»\t${r.identity}${r.catalogName ? `: 06_monsters «${r.catalogName}»` : ''}`),
      `Control de encuadre: ${p.framingIssues.length ? `${p.framingIssues.length} a revisar` : 'todos dentro de su celda'}`,
      ...p.framingIssues.map((r) => `  - ${r.id}\t${r.catalogName ?? r.sourceName}\t${r.framing}${r.framingNote ? ` · ${r.framingNote}` : ''}`),
      ...(run ? p.loadIssues.map((r) => `  - ${r.id}\t${r.catalogName ?? r.sourceName}\tla lámina ${r.sheet} no cargó`) : []),
      '',
    )
  })
  L.push(
    `## TOTAL`,
    `IDs distintos en los dos paquetes: ${a.unionIds} (referencia 285)${a.overlap.length ? ` · en ambos paquetes: ${a.overlap.join(', ')}` : ''}`,
    '',
    '## COMPROBACIONES PEDIDAS (recibido · carga · identidad · encuadre · asignado · visible)',
    ...a.checks.map((c) => `${c.id}\t${c.name ?? 'no existe en 06_monsters'}\t${c.cls ? clsLabel[c.cls] : '—'}\n  recibido: ${c.received}\n  carga: ${c.load}\n  identidad: ${c.identity}\n  encuadre: ${c.framing}\n  asignado: ${c.assigned}\n  visible: ${c.visible}`),
    '',
    `## PENDIENTES DEL CATÁLOGO PUBLICADO (06_monsters completo: ${a.catalogTotal}; con foto: ${a.withImage}; sin foto: ${a.pending.length})`,
    'Fotos del staff y encuadres aprobados conservados. No se excluye nada por ID alto, nivel ni falta de foto.',
    ...(['monstruo', 'npc', 'objeto'] as EntityClass[]).flatMap((c) => {
      const b = a.byClass(c)
      return [
        `### ${clsLabel[c]}: ${b.total} · con foto ${b.withImage} · sin foto ${b.pending.length}${c === 'objeto' ? ' (solo con clasificación documentada; la exportación no distingue objetos)' : ''}`,
        ...b.pending.map((p) => `- ${p.id}\t${p.name}`),
      ]
    }),
  )
  return L.join('\n')
}
